import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { useState } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Item, ItemContent } from "@volca/ui/components/ui/item";
import { LoadingCount } from "@volca/ui/components/ui/loading-count";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@volca/ui/components/ui/tabs";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { formatRelativeTime } from "@/core/utils/date";
import {
  EditCard,
  LoadError,
} from "@/payroll/ai-script/ai-script-persistent-edits/shared";
import { formatCoordinate } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/utils";
import { CellCommentThreadView } from "./cell-comment-thread";
import { useCellCommentThreads } from "./query";
import { CELL_COMMENT_PLACEMENT, type ICellCommentThread } from "./types";
import { useCellCommentActions } from "./use-cell-comment-actions";

const TAB = { open: "open", resolved: "resolved" } as const;
type CommentsTab = (typeof TAB)[keyof typeof TAB];

const isPlaced = (thread: ICellCommentThread) =>
  thread.placement.status === CELL_COMMENT_PLACEMENT.anchored ||
  thread.placement.status === CELL_COMMENT_PLACEMENT.positional;

const placementLabel = (thread: ICellCommentThread) => {
  const { row, col } = thread.placement;
  return isPlaced(thread) && row != null && col != null
    ? formatCoordinate([col - 1, row - 1])
    : null;
};

const lastActivity = (thread: ICellCommentThread) =>
  thread.comments.at(-1)?.created_at ?? thread.created_at;

const ThreadRow = ({
  thread,
  businessId,
  runId,
  onGoToThread,
}: {
  thread: ICellCommentThread;
  businessId: string | null;
  runId: string;
  onGoToThread: (thread: ICellCommentThread) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const actions = useCellCommentActions(businessId, runId);
  const first = thread.comments[0];
  const replies = thread.comments.length - 1;
  const address = placementLabel(thread);
  const activate = () =>
    address ? onGoToThread(thread) : setExpanded((value) => !value);

  return (
    <EditCard>
      {/* A thread on a cell opens on the grid, where its card holds the whole
          conversation. One whose cell is gone this run has nowhere to go, so it
          expands here instead. A real button, so the controls an expanded
          thread shows are not buried inside one. */}
      <Item
        render={<button type="button" />}
        onClick={activate}
        aria-expanded={address ? undefined : expanded}
        aria-label={
          address
            ? `Go to comment on ${thread.sheet_name} ${address}`
            : `${expanded ? "Hide" : "Show"} comment on ${thread.sheet_name}`
        }
        className="-m-1 w-auto min-w-0 p-1 text-left"
      >
        <ItemContent className="min-w-0 gap-2">
          <Stack className="w-full" direction="row" align="center" gap={2}>
            <Text
              variant="label"
              className="min-w-0 truncate"
              title={thread.sheet_name}
            >
              {thread.sheet_name}
            </Text>
            {address ? (
              <Text
                variant="caption"
                tone="muted"
                className="shrink-0 font-mono"
              >
                {address}
              </Text>
            ) : (
              <Badge variant="secondary" size="sm">
                Cell not found this run
              </Badge>
            )}
          </Stack>
          {!expanded && (
            <Text
              className="line-clamp-2 wrap-break-word whitespace-pre-wrap"
              tone={first?.deleted ? "muted" : "default"}
              title={
                first?.deleted ? "Comment deleted" : (first?.body ?? undefined)
              }
            >
              {first?.deleted ? "Comment deleted" : first?.body}
            </Text>
          )}
          <Text variant="caption" tone="muted">
            {[
              first?.author.full_name,
              replies > 0
                ? `${replies} ${replies === 1 ? "reply" : "replies"}`
                : null,
              formatRelativeTime(lastActivity(thread)),
            ]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        </ItemContent>
      </Item>
      {expanded && (
        <Stack className="w-full" gap={2} align="stretch">
          <CellCommentThreadView
            thread={thread}
            isUpdating={actions.isUpdating}
            onDeleteComment={(commentId) =>
              void actions.deleteComment(thread.id, commentId)
            }
            onReopen={() => void actions.reopen(thread.id)}
          />
          {!thread.resolved_at && (
            <Button
              variant="ghost"
              size="xs"
              className="self-start"
              disabled={actions.isUpdating}
              onClick={() => void actions.resolve(thread.id)}
            >
              <CheckIcon aria-hidden />
              Resolve
            </Button>
          )}
        </Stack>
      )}
    </EditCard>
  );
};

/**
 * Every comment thread on this pay period's workbook, open first. Picking one
 * takes the grid to its cell and opens its card there.
 */
export const CellCommentsSidebar = ({
  businessId,
  runId,
  onGoToThread,
  onClose,
}: {
  businessId: string | null;
  runId: string;
  onGoToThread: (thread: ICellCommentThread) => void;
  onClose: () => void;
}) => {
  const {
    data: threads,
    isLoading,
    isError,
    refetch,
  } = useCellCommentThreads(businessId, runId);
  const [tab, setTab] = useState<CommentsTab>(TAB.open);
  const sorted = [...(threads ?? [])].sort((left, right) =>
    lastActivity(right).localeCompare(lastActivity(left)),
  );
  const open = sorted.filter((thread) => !thread.resolved_at);
  const resolved = sorted.filter((thread) => !!thread.resolved_at);

  return (
    <Stack align="stretch" className="h-full min-h-0 w-full" gap={0}>
      <SimpleSidebarHeader
        title="Comments"
        description={
          open.length === 0
            ? "No open comments on this pay period"
            : `${open.length} open ${open.length === 1 ? "thread" : "threads"} on this pay period`
        }
        onClose={onClose}
      />

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as CommentsTab)}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <Stack align="start" gap={4} className="shrink-0 border-b">
          <TabsList variant="line">
            <TabsTrigger value={TAB.open}>
              Open
              <LoadingCount count={open.length} size="small" />
            </TabsTrigger>
            <TabsTrigger value={TAB.resolved}>
              Resolved
              <LoadingCount count={resolved.length} size="small" />
            </TabsTrigger>
          </TabsList>
        </Stack>
        {[TAB.open, TAB.resolved].map((panel) => (
          <TabsContent
            key={panel}
            value={panel}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              <Stack
                gap={2}
                align="stretch"
                className="min-h-full max-w-(--simple-sidebar-width,436px) px-5 py-5"
              >
                {isLoading ? (
                  <Text tone="muted">Loading comments…</Text>
                ) : isError ? (
                  <LoadError
                    message="Couldn't load comments."
                    onRetry={() => void refetch()}
                  />
                ) : (panel === TAB.open ? open : resolved).length === 0 ? (
                  <Empty className={emptyStateVariants({ layout: "inline" })}>
                    <EmptyHeader>
                      <EmptyDescription>
                        {panel === TAB.open
                          ? "Right-click a cell and choose Comment to start a thread."
                          : "Resolved threads show up here."}
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                ) : (
                  (panel === TAB.open ? open : resolved).map((thread) => (
                    <ThreadRow
                      key={thread.id}
                      thread={thread}
                      businessId={businessId}
                      runId={runId}
                      onGoToThread={onGoToThread}
                    />
                  ))
                )}
              </Stack>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </Stack>
  );
};
